import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronRight, MapPin, Tag, Ticket, Sparkles } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { SEO } from '../components/SEO';
import { EventCard } from '../components/EventCard';
import { Footer } from '../components/Footer';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { SeoLinkGroup } from '../components/seo/SeoLinks';
import { seoPath, seoHeading, seoTitle, seoBreadcrumbs, buildSeoJsonLd } from '../components/seo/seoPageData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Breadcrumbs = ({ crumbs, isDark }) => (
  <nav aria-label="Ruta de navegación" className="mb-6" data-testid="seo-breadcrumbs">
    <ol className={`flex flex-wrap items-center gap-1 text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
      {crumbs.map((c, i) => (
        <li key={c.path} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="w-3 h-3" />}
          {i < crumbs.length - 1
            ? <Link to={c.path} className="hover:text-[#00CC66] transition-colors">{c.name}</Link>
            : <span aria-current="page" className={isDark ? 'text-white/80' : 'text-gray-700'}>{c.name}</span>}
        </li>
      ))}
    </ol>
  </nav>
);

const StatPill = ({ icon: Icon, children, isDark, testId }) => (
  <span data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${isDark ? 'bg-white/5 text-white/80 ring-1 ring-white/10' : 'bg-white text-gray-700 ring-1 ring-gray-200'}`}>
    <Icon className="w-3.5 h-3.5 text-[#00CC66]" />
    {children}
  </span>
);

const Hero = ({ data, isDark }) => (
  <header className="max-w-3xl">
    <Breadcrumbs crumbs={seoBreadcrumbs(data)} isDark={isDark} />
    <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-6 ${isDark ? 'text-white' : 'text-[#064E3B]'}`} data-testid="seo-page-h1">
      {seoHeading(data)}
    </h1>
    <p className={`text-base md:text-lg leading-relaxed mb-6 ${isDark ? 'text-white/70' : 'text-gray-600'}`} data-testid="seo-page-intro">
      {data.intro}
    </p>
    <div className="flex flex-wrap gap-2">
      <StatPill icon={Ticket} isDark={isDark} testId="seo-page-total">{data.total} {data.total === 1 ? 'evento próximo' : 'eventos próximos'}</StatPill>
      {data.stats.free_count > 0 && <StatPill icon={Sparkles} isDark={isDark} testId="seo-page-free">{data.stats.free_count} gratis</StatPill>}
      {data.commune && <StatPill icon={MapPin} isDark={isDark} testId="seo-page-region">{data.commune.region_name}</StatPill>}
    </div>
  </header>
);

const EventsGrid = ({ data, isDark }) => {
  if (!data.events.length) {
    return (
      <div className={`rounded-3xl p-10 text-center ${isDark ? 'bg-white/5' : 'bg-white'}`} data-testid="seo-page-empty">
        <p className={isDark ? 'text-white/70' : 'text-gray-600'}>Por ahora no hay eventos próximos aquí. Vuelve pronto o explora otros panoramas.</p>
        <Button asChild className="btn-primary mt-6 rounded-full"><Link to="/explore" data-testid="seo-page-explore-link">Explorar panoramas</Link></Button>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="seo-page-events">
      {data.events.map((ev) => <EventCard key={ev.id} event={ev} />)}
    </div>
  );
};

const Related = ({ data, isDark }) => {
  const cats = (data.related.categories || []).map((c) => ({
    to: c.commune_slug ? `/panoramas/${c.commune_slug}/${c.category_slug}` : `/categoria/${c.slug}`,
    label: c.commune_slug ? `${c.category_name} en ${c.commune_name}` : c.name,
    count: c.count,
    testId: `seo-related-category-${c.category_slug || c.slug}`,
  }));
  const communes = (data.related.communes || []).map((c) => ({
    to: data.category ? `/panoramas/${c.slug}/${data.category.slug}` : `/panoramas/${c.slug}`,
    label: data.category ? `${data.category.name} en ${c.name}` : `Panoramas en ${c.name}`,
    count: c.count,
    testId: `seo-related-commune-${c.slug}`,
  }));
  if (!cats.length && !communes.length) return null;
  return (
    <section className={`mt-16 pt-10 border-t grid gap-10 md:grid-cols-2 ${isDark ? 'border-white/10' : 'border-gray-200'}`} data-testid="seo-related">
      <SeoLinkGroup title="Por categoría" icon={Tag} items={cats} isDark={isDark} testId="seo-related-categories" />
      <SeoLinkGroup title={data.category ? 'En otras comunas' : 'Comunas cercanas'} icon={MapPin} items={communes} isDark={isDark} testId="seo-related-communes" />
    </section>
  );
};

const LoadingState = () => (
  <div className="max-w-7xl mx-auto px-4 md:px-8 py-16" data-testid="seo-page-loading">
    <Skeleton className="h-14 w-2/3 mb-6 rounded-2xl" />
    <Skeleton className="h-24 w-full max-w-3xl mb-10 rounded-2xl" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-72 rounded-3xl" />)}
    </div>
  </div>
);

export default function SeoLanding() {
  const { comuna, categoria } = useParams();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    setFailed(false);
    axios.get(`${API_URL}/api/seo/page`, { params: { comuna, categoria } })
      .then((r) => setData(r.data))
      .catch((err) => (err.response?.status === 404 ? setNotFound(true) : setFailed(true)));
  }, [comuna, categoria, attempt]);

  const pageCls = `min-h-screen ${isDark ? 'night-page text-white' : 'bg-[#F8FAF9]'}`;

  if (failed) {
    return (
      <div className={pageCls} data-testid="seo-page-error">
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <p className="text-2xl font-bold mb-4">No pudimos cargar los panoramas</p>
          <Button onClick={() => setAttempt((a) => a + 1)} className="btn-primary rounded-full" data-testid="seo-page-retry-btn">Reintentar</Button>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={pageCls} data-testid="seo-page-not-found">
        <SEO title="Página no encontrada" noIndex />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <p className="text-2xl font-bold mb-4">No encontramos esta página</p>
          <Button asChild className="btn-primary rounded-full"><Link to="/explore">Explorar panoramas</Link></Button>
        </div>
        <Footer />
      </div>
    );
  }
  if (!data) return <div className={pageCls}><LoadingState /></div>;

  return (
    <div className={pageCls} data-testid="seo-landing-page">
      <SEO
        title={seoTitle(data)}
        description={data.intro.length > 158 ? `${data.intro.slice(0, 155).trimEnd()}…` : data.intro}
        path={seoPath(data)}
        image={data.events[0]?.main_image}
        jsonLd={buildSeoJsonLd(data)}
      />
      <main className="max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-16">
        <Hero data={data} isDark={isDark} />
        <div className="mt-12"><EventsGrid data={data} isDark={isDark} /></div>
        <Related data={data} isDark={isDark} />
      </main>
      <Footer />
    </div>
  );
}
